import React from 'react'
import {
  Typography,
  IconButton,
  Grid,
  makeStyles,
  createStyles, Chip, Box
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import { Controller, useFormContext } from 'react-hook-form'
import { TExistingFile, TNewFile } from '../../../widgets/productForm/productForm.types'

interface FormImagePreviewProps {
  name: string;

  onAdd?: (file: File) => void;

  onRemove?: () => void;

  isMain?: boolean;
}

type TImageField = TNewFile | TExistingFile | null

export const FormImagePreview = React.memo(({name, onAdd, onRemove, isMain}: FormImagePreviewProps) => {
  const classes = useStyles()

  const {control} = useFormContext()

  const getPreviewUrl = (value: TImageField): string | null => {
    if (!value) {
      return null
    }
    if ('image' in value) {
      return URL.createObjectURL(value.image)
    }
    if ('url' in value) {
      return value.url
    }
    return null
  }

  const getImageName = (value: TImageField): string => {
    if (!value) {
      return ''
    }
    if ('image' in value) {
      return value.image.name
    }
    if ('name' in value) {
      return `${value.name} (загружено)`
    }
    return ''
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({field}) => {
        const handleRemove = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          e.stopPropagation()
          if (onRemove) {
            onRemove()
          } else {
            field.onChange(null)
          }
        }

        const previewUrl = getPreviewUrl(field.value)
        const nameToDisplay = getImageName(field.value)

        return (
          <>
            {previewUrl && (
              <Grid container alignItems="center" className={classes.previewItem}>
                <img
                  src={previewUrl}
                  alt={nameToDisplay}
                  className={classes.previewImage}
                />
                <Box className={classes.labelWrapper}>
                  <Typography
                    noWrap
                    className={classes.filenameText}
                    title={nameToDisplay}
                  >
                    {nameToDisplay}
                  </Typography>
                </Box>
                {isMain && (
                  <Chip
                    label="Главная"
                    color="primary"
                    size="small"
                    className={classes.mainChip}
                  />
                )}
                <IconButton onClick={handleRemove} className={classes.removeIcon}>
                  <DeleteIcon fontSize="small"/>
                </IconButton>
              </Grid>
            )}
          </>
        )
      }}
    />
  )
})

const useStyles = makeStyles(() =>
  createStyles({
    dropzone: {
      border: '2px dashed #ced4da',
      borderRadius: 8,
      padding: 16,
      textAlign: 'center',
      backgroundColor: '#fff',
      cursor: 'pointer',
      transition: 'background 0.2s',
      '&.active': {
        backgroundColor: '#f0f4ff'
      }
    },
    previewItem: {
      display: 'flex',
      alignItems: 'center',
      borderRadius: 4,
      position: 'relative',
      overflow: 'hidden',
      height: '100%'
    },
    mainImage: {
      height: 288,
      gridRowStart: 'span 2',
      gridColumnStart: 'span 2'
    },
    labelWrapper: {
      position: 'absolute',
      bottom: '0',
      left: '0',
      width: 'calc(100% - 16px)',
      padding: 8,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      color: 'white'
    },
    filenameText: {
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      paddingRight: '8px'
    },
    previewImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      borderRadius: 4
    },
    mainChip: {
      position: 'absolute',
      right: 50,
      top: 10,
      fontWeight: 'bold',
      fontSize: 12,
      backgroundColor: '#1976d2',
      color: '#fff'
    },
    removeIcon: {
      position: 'absolute',
      padding: 2,
      color: '#bc3838',
      backgroundColor: '#ffdbdb',
      right: 10,
      top: 10,
      '&:hover': {
        backgroundColor: '#ffdbdb'
      }
    }
  })
)